/**
 * PowerCard Component
 * Mobile card display for individual powers in a report
 */

import {
  PencilIcon,
  TrashIcon,
  CameraIcon,
  CheckCircleIcon,
  UserIcon,
  MapPinIcon,
  CalendarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatDate, toTitleCase } from '../utils';

// Capitalize first letter of a string
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const PowerCard = ({
  power,
  onEdit,
  onDelete,
  onUploadProof,
  isSubmitted = false,
  reportStatus = 'DRAFT'
}) => {
  const isLegacy = power.powerReportPowerId === 0;
  const canEdit = !isLegacy && !isSubmitted;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-3 sm:p-4">
      {/* Header Row */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="h-4 w-4 text-gray-400" />
            <h3 className="font-mono font-bold text-gray-900 dark:text-white">
              {power.powerNumber || 'N/A'}
            </h3>
            {power.isRewrite && (
              <span
                className="inline-flex items-center text-xs font-bold text-amber-600 dark:text-amber-400"
                title={`Rewrite of ${power.rewriteFromPowerNumber}${power.rewriteFromBondAmount ? ` ($${formatCurrency(power.rewriteFromBondAmount)})` : ''}`}
              >
                RW
              </span>
            )}
            {power.hasProof && (
              <CheckCircleIcon className="h-4 w-4 text-green-500" title="Has proof" />
            )}
          </div>
          {power.defendantFirstName && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 flex items-center gap-1">
              <UserIcon className="h-3.5 w-3.5" />
              {capitalizeFirst(power.defendantFirstName)} {capitalizeFirst(power.defendantLastName)}
            </p>
          )}
        </div>

        {/* Action Buttons — tighter on mobile so the power # and defendant don't get crowded */}
        <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
          {/* Proof Button */}
          {!isLegacy && (
            <button
              onClick={() => onUploadProof && onUploadProof(power)}
              className={`p-1.5 sm:p-2 rounded-full transition-colors ${
                power.hasProof
                  ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                  : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={power.hasProof ? 'View/Update Proof' : 'Upload Proof'}
            >
              <CameraIcon className="h-5 w-5" />
            </button>
          )}

          {/* Edit Button */}
          {canEdit && (
            <button
              onClick={() => onEdit(power)}
              className="p-1.5 sm:p-2 text-primary dark:text-primary-light hover:bg-primary/10 rounded-full transition-colors"
              title="Edit Power"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          )}

          {/* Delete Button */}
          {canEdit && (
            <button
              onClick={() => onDelete(power.powerReportPowerId)}
              className="p-1.5 sm:p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
              title="Delete Power"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        {/* Agent */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Agent</p>
          <p className="text-sm text-gray-900 dark:text-white">
            {power.executingAgentFirstName || ''} {power.executingAgentLastName || 'N/A'}
          </p>
        </div>

        {/* City */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 flex items-center gap-1">
            <MapPinIcon className="h-3 w-3" />
            City
          </p>
          <p className="text-sm text-gray-900 dark:text-white">
            {power.cityName && power.stateCode
              ? `${toTitleCase(power.cityName)}, ${power.stateCode}`
              : 'N/A'}
          </p>
        </div>

        {/* Execution Date */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 flex items-center gap-1">
            <CalendarIcon className="h-3 w-3" />
            Exec Date
          </p>
          <p className="text-sm text-gray-900 dark:text-white">
            {formatDate(power.executionDate)}
          </p>
        </div>

        {/* Case Number */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Case #</p>
          <p className="text-sm text-gray-900 dark:text-white">
            {power.caseNumber || 'N/A'}
          </p>
        </div>
      </div>

      {/* Amounts — single-column label/value rows on mobile so currency values never get cramped;
          4-col grid from sm+ where horizontal space allows. */}
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        {/* Mobile: stacked rows */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700 text-sm">
          <div className="flex justify-between gap-3 py-1.5">
            <span className="text-gray-500 dark:text-gray-400">Amount</span>
            <span className="font-medium text-gray-900 dark:text-white tabular-nums">
              ${formatCurrency(power.powerAmount)}
            </span>
          </div>
          <div className="flex justify-between gap-3 py-1.5">
            <span className="text-gray-500 dark:text-gray-400">NET</span>
            <span className={`font-medium tabular-nums ${
              power.isRewrite
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-primary dark:text-primary-light'
            }`}>
              ${formatCurrency(power.commission)}
            </span>
          </div>
          <div className="flex justify-between gap-3 py-1.5">
            <span className="text-gray-500 dark:text-gray-400">BUF</span>
            <span className={`font-medium tabular-nums ${
              power.isRewrite
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-primary dark:text-primary-light'
            }`}>
              ${formatCurrency(power.buf)}
            </span>
          </div>
          <div className="flex justify-between gap-3 py-1.5">
            <span className="text-gray-500 dark:text-gray-400">Premium</span>
            <span className="font-bold text-green-600 dark:text-green-400 tabular-nums">
              ${formatCurrency(power.premium)}
            </span>
          </div>
        </div>

        {/* Desktop: 4-col grid */}
        <div className="hidden sm:grid grid-cols-4 gap-2">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Amount</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              ${formatCurrency(power.powerAmount)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">NET</p>
            <p className={`text-sm font-medium ${
              power.isRewrite
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-primary dark:text-primary-light'
            }`}>
              ${formatCurrency(power.commission)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">BUF</p>
            <p className={`text-sm font-medium ${
              power.isRewrite
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-primary dark:text-primary-light'
            }`}>
              ${formatCurrency(power.buf)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Premium</p>
            <p className="text-sm font-bold text-green-600 dark:text-green-400">
              ${formatCurrency(power.premium)}
            </p>
          </div>
        </div>
      </div>

      {/* Legacy Power Indicator */}
      {isLegacy && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs text-amber-600 dark:text-amber-400 italic">
            Legacy power - editing disabled
          </p>
        </div>
      )}
    </div>
  );
};

export default PowerCard;
